
import React, { useRef } from 'react';
import { useIntersectionObserver } from '../hooks/useIntersectionObserver';
import { ZapIcon, PricingIcon, CodeIcon, GitBranchIcon, DatabaseIcon, RocketIcon } from './Icons';

const features = [
  {
    icon: <ZapIcon />,
    title: 'Instant Code Generation',
    description: 'From prompt to production-ready code in moments. Accelerate your development cycle instantly.'
  },
  {
    icon: <PricingIcon />,
    title: 'Affordable Plans',
    description: 'Start for free and scale up with cost-effective plans. High-end AI tools for every budget.'
  },
  {
    icon: <CodeIcon />,
    title: 'Real-Time Previews',
    description: 'See your application come to life as the AI builds it. Provide feedback and watch it adapt instantly.'
  },
  {
    icon: <GitBranchIcon />,
    title: 'Iterative AI Editing',
    description: 'Refine your design with simple commands. Tell Codey to change colors, text, layouts, and more.'
  },
  {
    icon: <DatabaseIcon />,
    title: 'Backend & API Ready',
    description: 'Codey automatically detects when you need a database or AI features and prepares the code for integration.'
  },
  {
    icon: <RocketIcon />,
    title: 'All-In-One Solution',
    description: 'From idea to deployment. Our premium service handles complex apps, backend setup, and hosting for you.'
  }
];

const FeatureCard: React.FC<{ icon: React.ReactNode, title: string, description: string, delay: number }> = ({ icon, title, description, delay }) => {
    const cardRef = useRef<HTMLDivElement>(null);
    const isVisible = useIntersectionObserver(cardRef, { threshold: 0.1 });

    return (
        <div 
            ref={cardRef}
            className={`glow-card group p-6 rounded-xl bg-[var(--panel-bg)] backdrop-blur-sm relative border border-[var(--border-color)] transition-all duration-700 hover:-translate-y-2 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
            style={{ transitionDelay: `${delay}ms` }}
        >
            <div className="relative z-10">
                <div className="mb-4 w-12 h-12 flex items-center justify-center rounded-lg bg-gradient-to-br from-[var(--gradient-from)] to-[var(--gradient-to)] text-white">
                    {icon}
                </div>
                <h3 className="text-xl font-bold text-[var(--text-primary)] mb-2">{title}</h3>
                <p className="text-[var(--text-secondary)]">{description}</p>
            </div>
        </div>
    );
}

const Features: React.FC = () => {
    const sectionRef = useRef<HTMLDivElement>(null);
    const isVisible = useIntersectionObserver(sectionRef, { threshold: 0.1 });
  
  return (
    <section id="templates" ref={sectionRef} className={`py-20 sm:py-32 transition-opacity duration-1000 ${isVisible ? 'opacity-100' : 'opacity-0'}`}>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-extrabold text-[var(--text-primary)]">Why Choose Codemine?</h2>
          <p className="mt-4 max-w-2xl mx-auto text-lg text-[var(--text-secondary)]">Everything you need to build and scale your web application, powered by AI.</p>
        </div>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {features.map((feature, index) => (
            <FeatureCard 
              key={index}
              icon={feature.icon}
              title={feature.title}
              description={feature.description}
              delay={index * 100}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Features;